// Article Categories Configuration
export interface ArticleCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  color: string;
  icon: string; // name from components/ui/Icon
}

export const ARTICLE_CATEGORIES: ArticleCategory[] = [
  {
    id: 'seo',
    name: 'SEO',
    slug: 'seo',
    description: 'Search Engine Optimization strategies and insights',
    color: '#0B1F3B',
    icon: 'search'
  },
  {
    id: 'analytics',
    name: 'Analytics',
    slug: 'analytics', 
    description: 'Data analysis, tracking, and measurement strategies',
    color: '#0B1F3B',
    icon: 'chart'
  },
  {
    id: 'conversion-optimization',
    name: 'Conversion Optimization',
    slug: 'conversion-optimization',
    description: 'CRO tactics and A/B testing insights',
    color: '#0B1F3B',
    icon: 'trend'
  },
  {
    id: 'digital-marketing',
    name: 'Digital Marketing',
    slug: 'digital-marketing',
    description: 'General digital marketing strategies and trends',
    color: '#0B1F3B',
    icon: 'target'
  },
  {
    id: 'technology',
    name: 'Technology',
    slug: 'technology',
    description: 'Marketing technology, tools, and platforms',
    color: '#0B1F3B',
    icon: 'layers'
  },
  {
    id: 'case-studies',
    name: 'Case Studies',
    slug: 'case-studies',
    description: 'Real-world project results and learnings',
    color: '#0B1F3B',
    icon: 'document'
  },
  {
    id: 'industry-insights',
    name: 'Industry Insights',
    slug: 'industry-insights',
    description: 'Industry trends and future predictions',
    color: '#0B1F3B',
    icon: 'spark'
  }
];

export const getCategoryBySlug = (slug: string): ArticleCategory | undefined => {
  return ARTICLE_CATEGORIES.find(category => category.slug === slug);
};

export const getCategoryById = (id: string): ArticleCategory | undefined => {
  return ARTICLE_CATEGORIES.find(category => category.id === id);
};

export const getAllCategories = (): ArticleCategory[] => {
  return ARTICLE_CATEGORIES;
};

// Helper function to extract category from tags
export const extractCategoryFromTags = (tags: string[] = []): ArticleCategory | null => {
  for (const tag of tags) {
    const category = ARTICLE_CATEGORIES.find(cat => 
      cat.slug === tag.toLowerCase() || 
      cat.name.toLowerCase() === tag.toLowerCase() ||
      cat.id === tag.toLowerCase()
    );
    if (category) return category;
  }
  return null;
};

// Function to suggest categories based on title and content
export const suggestCategory = (title: string, content: string, tags: string[] = []): ArticleCategory | null => {
  const text = `${title} ${content} ${tags.join(' ')}`.toLowerCase();
  
  // Priority order for keyword matching
  const categoryKeywords = {
    'seo': ['seo', 'search engine', 'organic traffic', 'keywords', 'serp', 'ranking'],
    'analytics': ['analytics', 'data', 'tracking', 'metrics', 'measurement', 'ga4', 'google analytics'],
    'conversion-optimization': ['conversion', 'cro', 'a/b test', 'optimization', 'funnel', 'landing page'],
    'technology': ['technology', 'tool', 'platform', 'software', 'tech stack', 'api'],
    'case-studies': ['case study', 'results', 'success story', 'project', 'client work'],
    'digital-marketing': ['marketing', 'campaign', 'strategy', 'advertising', 'promotion'],
    'industry-insights': ['trends', 'future', 'industry', 'insights', 'predictions', 'outlook']
  };
  
  for (const [categoryId, keywords] of Object.entries(categoryKeywords)) {
    if (keywords.some(keyword => text.includes(keyword))) {
      return getCategoryById(categoryId) || null;
    }
  }
  
  return null;
};
